import { useEffect, useState } from 'react';
import { toast } from 'react-toastify';
import { useSelector } from 'react-redux';
import { Box, Grid, IconButton, Typography } from '@mui/material';
import { RefreshOutlined } from '@mui/icons-material';

import { FHButton, FHConfirmDialog, FHInputField } from '../../components/base';
import EditOutlinedIcon from '../../components/svgs/EditOutlinedIcon';
import { FORTA_BLUE } from '../../utils/colors';
import { FORTA_WHITE } from '../../utils/colors';
import { defaultServiceCodeDescriptions } from '../constants/general';
import { HcpcsServiceCodeMapping, ServiceCodeTypes } from '../../constants';

import type { RootState } from '../../store';
import type { IServiceCodeDescription } from '../../utils/types';

interface PayorServiceCodeLabelRowProps {
  serviceCode: IServiceCodeDescription;
  preferredServiceCodeType: string;
  isEditing: boolean;
  isDisabled: boolean;
  onEdit: () => void;
  onCancel: () => void;
  onSave: (editableServiceCode: IServiceCodeDescription) => void;
  onReset: (editableServiceCode: IServiceCodeDescription) => void;
}

function PayorServiceCodeLabelRow({
  serviceCode,
  preferredServiceCodeType,
  isEditing,
  isDisabled,
  onEdit,
  onCancel,
  onSave,
  onReset,
}: PayorServiceCodeLabelRowProps) {
  const { list: serviceCodes } = useSelector((state: RootState) => state.serviceCodes);
  const serviceCodeRow = serviceCodes.find((code) => code.id === serviceCode.service_code_id);
  const displayedServiceCode =
    preferredServiceCodeType === ServiceCodeTypes.Hcpcs
      ? Object.entries(HcpcsServiceCodeMapping).find(
          ([code]) => code === serviceCodeRow?.code,
        )?.[1]
      : serviceCodeRow?.code;
  const defaultDescription = Object.entries(defaultServiceCodeDescriptions).find(
    ([code]) => code === serviceCode.service_code,
  )?.[1];
  const [editableServiceCode, setEditableServiceCode] = useState({ ...serviceCode });
  const [showConfirmDialog, setShowConfirmDialog] = useState(false);
  useEffect(() => {
    setEditableServiceCode({ ...serviceCode });
  }, [serviceCode]);

  const handleSave = (editableServiceCode: IServiceCodeDescription) => {
    if (editableServiceCode.description.trim().length < 2) {
      toast.error('Description must be at least 2 characters long');
    } else {
      onSave(editableServiceCode);
    }
  };

  return (
    <Grid
      container
      p={1}
      sx={{
        transition: 'background-color 0.2s ease',
        '&:hover': {
          backgroundColor: isEditing ? 'transparent' : FORTA_WHITE,
          borderRadius: '10px',
        },
        cursor: isEditing ? 'default' : 'pointer',
      }}
    >
      <Grid item xs={1.5} display="flex" alignItems="center" justifyContent="flex-start" mx={2}>
        <Typography>
          {preferredServiceCodeType === ServiceCodeTypes.Hcpcs
            ? displayedServiceCode
            : serviceCodeRow?.code}
        </Typography>
      </Grid>
      <Grid item xs={6} display="flex" alignItems="center" justifyContent="flex-start" mr={3}>
        {isEditing ? (
          <FHInputField
            inputProps={{ 'data-testid': 'description' }}
            fullWidth
            inputHeight={40}
            sx={{ width: '100%', boxShadow: 'none' }}
            value={editableServiceCode.description}
            onChange={(e) =>
              setEditableServiceCode({ ...editableServiceCode, description: e.target.value })
            }
          />
        ) : (
          <Typography textAlign={'left'}>{serviceCode.description}</Typography>
        )}
      </Grid>
      <Grid item xs={1} display="flex" alignItems="center" justifyContent="flex-start">
        {isEditing ? (
          <FHButton
            data-testid="saveServiceLabelButton"
            disabled={isDisabled}
            onClick={() => handleSave(editableServiceCode)}
          >
            Save
          </FHButton>
        ) : (
          <IconButton
            data-testid="editServiceLabelButton"
            sx={{
              width: 48,
              height: 30,
              border: `2px solid ${FORTA_BLUE.primary}`,
              borderRadius: '20px',
              backgroundColor: isEditing ? FORTA_BLUE.primary : 'transparent',
              mr: 3,
              '&:hover': {
                backgroundColor: isEditing ? FORTA_BLUE.primary : 'transparent',
              },
            }}
            onClick={onEdit}
          >
            <EditOutlinedIcon color={isEditing ? FORTA_WHITE : FORTA_BLUE.primary} width={18} />
          </IconButton>
        )}
      </Grid>
      <Grid item xs={1} display="flex" alignItems="center" justifyContent="flex-start">
        {isEditing ? (
          <FHButton data-testid="cancelServiceLabelButton" onClick={onCancel}>
            Cancel
          </FHButton>
        ) : (
          serviceCode.id &&
          serviceCode.description !== defaultDescription && (
            <IconButton
              data-testid="editServiceLabelButton"
              sx={{
                width: 200,
                height: 32,
                border: `2px solid ${FORTA_BLUE.primary}`,
                borderRadius: '20px',
                backgroundColor: isEditing ? FORTA_BLUE.primary : 'transparent',
                mr: 3,
              }}
              onClick={() => setShowConfirmDialog(true)}
            >
              <RefreshOutlined sx={{ color: isEditing ? FORTA_WHITE : FORTA_BLUE.primary }} />
              <Typography ml={1} color={FORTA_BLUE.primary}>
                Reset to default
              </Typography>
            </IconButton>
          )
        )}
      </Grid>
      <FHConfirmDialog
        open={showConfirmDialog}
        title={'Reset to default'}
        contentRenderer={() => (
          <Box
            sx={{
              height: '150px',
              display: 'flex',
              justifyContent: 'center',
              alignItems: 'center',
              mb: 1,
            }}
          >
            <Box sx={{ textAlign: 'center', fontSize: 21, padding: '48px 0px 48px 0px' }}>
              <Typography sx={{ fontWeight: 500, fontSize: 21, mb: 2, lineHeight: 1.5 }}>
                Are you sure you want to reset{' '}
                <b>
                  {preferredServiceCodeType === ServiceCodeTypes.Hcpcs
                    ? displayedServiceCode
                    : serviceCodeRow?.code}
                </b>{' '}
                service code <br /> to its default description?
              </Typography>
              <Typography sx={{ fontWeight: 500, fontSize: 21 }}>
                It will be reset to -{' '}
                <b>
                  {defaultDescription}
                </b>
              </Typography>
            </Box>
          </Box>
        )}
        submittext={'Reset'}
        canceltext={'Cancel'}
        onClose={() => setShowConfirmDialog(false)}
        onSubmit={() => {
          setShowConfirmDialog(false);
          onReset(serviceCode);
        }}
      />
    </Grid>
  );
}

export default PayorServiceCodeLabelRow;
